"use client"

import Loader from "@/app/(site)/_components/Atoms/Loader"
import HeadingElement from "@/app/(site)/_components/GCMolecules/HeadingElement"
import RegularProductCard from "@/app/(site)/_components/Organisms/Cards/ProductCards/RegularProductCard"
import AnimationSliderWrapper from "@/app/(site)/_components/SanityBlocks/Carousel/AnimationSliderWrapper"
import DepictCarouselTrackingScript from "@/app/(site)/_components/SanityBlocks/DepictCarousel/DepictCarouselTrackingScript"
import { useFetchRecommendedProducts } from "@/app/(site)/_utils/_hooks/useFetchRecommendedProducts"
import { RecommendationType } from "@/lib/depict-client/types/RecommendationType"
import { isContentVisible } from "@/src/utils/content/isContentVisible"
import { getLangCode } from "@/src/utils/string/getLangCode"
import { t } from "@/translations"
import React, { useMemo } from "react"

type DepictCarouselProps = {
    data?: any
    isPdp?: boolean
    params: { slug: string; locale: string }
    isAboveTheFold?: boolean
    carouselIndex?: number
}

const DepictCarousel: React.FC<DepictCarouselProps> = ({
    data,
    isPdp,
    params,
    isAboveTheFold = false,
    carouselIndex
}) => {
    const langCode = getLangCode(params.locale) || "" // params from contentMapper and langCode from page

    const depictType = data?.depictRecommendationType || RecommendationType.FOR_YOU

    const productIds = useMemo(() => {
        const excludeProductIds =
            depictType === RecommendationType.LANDING_PAGE ||
            depictType === RecommendationType.RECENTLY_VIEWED ||
            depictType === RecommendationType.FOR_YOU

        if (excludeProductIds) {
            return undefined
        }

        return (
            data?.items
                ?.filter(
                    (item: { depictProductDisplayId?: string }) =>
                        item?.depictProductDisplayId
                )
                .map(
                    (item: { depictProductDisplayId: string }) =>
                        item.depictProductDisplayId
                ) || []
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data?.items?.map((item: any) => item.depictProductDisplayId))])

    const { groupedProductsDataRecord, loading } = useFetchRecommendedProducts({
        productIds,
        depictType,
        paramsLocale: params.locale
    })

    if (!data) return null

    if (loading) return <Loader />

    if (
        !groupedProductsDataRecord ||
        Object.values(groupedProductsDataRecord).every(
            value => !Array.isArray(value) || value.length === 0
        )
    ) {
        return
    }

    /**
     * This is done in the ContentMapper, but we need to do it here as well for Product-page
     */
    if (isPdp && !isContentVisible(data, params.locale)) {
        return null
    }

    const carouselTitle =
        data?.title?.[langCode] ||
        data?.title?.["en"] ||
        data?.title ||
        t(langCode, "product_page.recommended_products")

    const trackingTitle = data.englishTitle || data?.title?.en || carouselTitle

    const carouselId = `depict-carousel-${carouselTitle}-${carouselIndex || 0}`
        .toLowerCase()
        .replace(/\s+/g, "-")

    // Build visible product groups and products (adjust .slice(0, 9) if needed)
    const visibleProductGroups = Object.values(groupedProductsDataRecord)
        .filter(Array.isArray)
        .slice(0, 9)
    // Only take the first product from each group for tracking (matching what's rendered)
    const visibleProducts = visibleProductGroups.map(group => group[0]).filter(Boolean)

    return (
        <>
            <section
                id={carouselId}
                className="flex flex-col gap-32 px-15 py-32 md:px-24 md:py-56"
            >
                <HeadingElement
                    text={carouselTitle}
                    type="h2"
                    isAboveTheFold={false}
                    styleKey="font_trimMonoPoc_Header_24_130_-4_400_uppercase"
                />
                <AnimationSliderWrapper langCode={langCode}>
                    {visibleProductGroups.map((productGroup, index) =>
                        Array.isArray(productGroup) ? (
                            <div
                                key={index}
                                className={
                                    "relative flex w-250 flex-shrink-0 flex-col justify-between md:w-390"
                                }
                            >
                                <RegularProductCard
                                    isAboveTheFold={isAboveTheFold}
                                    productVariants={productGroup}
                                    listName="carousel"
                                    listId={`Homepage|${trackingTitle}`}
                                    listType="carousel"
                                    listHierarchy={`Homepage|${trackingTitle}`}
                                    index={index}
                                />
                            </div>
                        ) : null
                    )}
                </AnimationSliderWrapper>
            </section>

            {/* Tracking Scripts */}
            <DepictCarouselTrackingScript
                carouselId={carouselId}
                carouselTitle={trackingTitle}
                productItems={visibleProducts}
                locale={params.locale}
            />
        </>
    )
}

export default DepictCarousel
